import { ROOT_KEYS, ROOT_TITLES, hostOf, isRootId, rootId, rootKeyOf, type BookmarkRow, type MaterializedTree } from '@bsync/shared';

export const ROOT_IDS = ROOT_KEYS.map(rootId);

export function titleOf(tree: MaterializedTree, id: string): string {
  const key = rootKeyOf(id);
  if (key) return ROOT_TITLES[key];
  const r = tree.rows.get(id);
  if (!r) return '';
  if (r.kind === 'folder') return r.title || 'Untitled folder';
  return r.title || r.url || 'Untitled';
}

/** Folder chain from a root down to `id` (inclusive). */
export function pathTo(tree: MaterializedTree, id: string): string[] {
  const out = [id];
  let cur = id;
  for (let guard = 0; !isRootId(cur) && guard < 1000; guard++) {
    const p = tree.parentOf.get(cur);
    if (!p) break;
    out.unshift(p);
    cur = p;
  }
  return out;
}

export function isFolderLike(tree: MaterializedTree, id: string): boolean {
  return isRootId(id) || tree.rows.get(id)?.kind === 'folder';
}

export function childFolders(tree: MaterializedTree, id: string): string[] {
  return (tree.childrenOf.get(id) ?? []).filter((c) => tree.rows.get(c)?.kind === 'folder');
}

export function countIn(tree: MaterializedTree, id: string): number {
  let n = 0;
  const stack = [...(tree.childrenOf.get(id) ?? [])];
  while (stack.length) {
    const c = stack.pop()!;
    const r = tree.rows.get(c);
    if (r?.kind === 'bookmark') n++;
    else if (r?.kind === 'folder') stack.push(...(tree.childrenOf.get(c) ?? []));
  }
  return n;
}

export interface SearchHit {
  row: BookmarkRow;
  path: string[];
  score: number;
}

/** Case-insensitive search over titles, URLs and folder names; all terms must match. */
export function search(tree: MaterializedTree, query: string, limit = 200): SearchHit[] {
  const terms = query.toLowerCase().split(/\s+/).filter(Boolean);
  if (!terms.length) return [];
  const hits: SearchHit[] = [];
  for (const [id] of tree.parentOf) {
    const row = tree.rows.get(id)!;
    if (row.kind === 'separator') continue;
    const title = row.title.toLowerCase();
    const url = (row.url ?? '').toLowerCase();
    let score = 0;
    let ok = true;
    for (const t of terms) {
      if (title.startsWith(t)) score += 3;
      else if (title.includes(t)) score += 2;
      else if (url.includes(t)) score += 1;
      else {
        ok = false;
        break;
      }
    }
    if (ok) hits.push({ row, path: pathTo(tree, id).slice(0, -1), score: score + (row.kind === 'folder' ? 0.5 : 0) });
  }
  return hits.sort((a, b) => b.score - a.score || a.row.title.localeCompare(b.row.title)).slice(0, limit);
}

const TILE_HUES = [212, 262, 330, 18, 42, 152, 188, 290];

/** Deterministic letter tile for a bookmark (no third-party favicon requests). */
export function tileFor(url: string | null): { letter: string; hue: number } {
  const host = hostOf(url) || '?';
  let h = 0;
  for (const ch of host) h = (h * 31 + ch.charCodeAt(0)) >>> 0;
  return { letter: host[0]!.toUpperCase(), hue: TILE_HUES[h % TILE_HUES.length]! };
}
